// v77.20 — Theme provider.
//
// Reads the user's `appearance_preference` ("system" | "light" | "dark")
// and applies the matching colour scheme to the whole app by setting the
// `data-theme` attribute on <html>. Components either inherit Tailwind's
// `dark:` variant (where authored) or pick up overrides defined in
// `index.dark.css` keyed off `[data-theme="dark"]`.
//
// "system" follows the OS / browser preference live — flipping the OS
// theme updates the app without a reload. We persist the raw preference
// (not the resolved scheme) so the user's choice survives device changes.
//
// Eager localStorage write: we write the resolved scheme to localStorage
// on every change so the bootstrap script in index.html can apply the
// theme BEFORE React mounts, eliminating the white-flash on dark-mode
// users' page loads.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const STORAGE_KEY = "cpc_appearance_preference";
const ThemeCtx = createContext({
  preference: "system",
  resolved: "light",
  setPreference: () => {},
});

const VALID = new Set(["system", "light", "dark"]);

function applyResolved(scheme) {
  const root = document.documentElement;
  if (scheme === "dark") {
    root.setAttribute("data-theme", "dark");
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  } else {
    root.setAttribute("data-theme", "light");
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  }
}

function readMediaScheme() {
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function ThemeProvider({ children }) {
  const { user, refresh } = useAuth();

  // Initial preference: localStorage > server default. Reading server
  // happens via useAuth; we sync into local state when it loads.
  const [preference, _setPreference] = useState(() => {
    const ls = (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "";
    return VALID.has(ls) ? ls : "system";
  });

  // Sync preference from server once user loads.
  useEffect(() => {
    const fromUser = user?.appearance_preference;
    if (fromUser && VALID.has(fromUser) && fromUser !== preference) {
      _setPreference(fromUser);
      try { localStorage.setItem(STORAGE_KEY, fromUser); } catch { /* localStorage quota or disabled — ignore */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.appearance_preference]);

  // Resolve preference -> "light" | "dark" and apply to <html>.
  const resolved = useMemo(() => {
    if (preference === "dark") return "dark";
    if (preference === "light") return "light";
    return readMediaScheme();
  }, [preference]);

  useEffect(() => {
    applyResolved(resolved);
  }, [resolved]);

  // Live-follow OS scheme when preference is "system".
  useEffect(() => {
    if (preference !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyResolved(readMediaScheme());
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, [preference]);

  const setPreference = async (next) => {
    if (!VALID.has(next)) return;
    _setPreference(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* localStorage quota or disabled — ignore */ }
    if (user) {
      try {
        await api.patch("/me/appearance", { appearance_preference: next });
        // Refresh the user object so /auth/me reflects the new value
        // for any other consumer.
        if (refresh) refresh();
      } catch (e) {
        // Persist locally even if backend update fails — better UX.
      }
    }
  };

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved],
  );
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  return useContext(ThemeCtx);
}
